import { useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Tag, TooltipTag } from 'sonata-react-components';

import { useDriveToStoreNavigation } from '@/components/driveToStore/common/hooks/useDriveToStoreNavigation';
import LineItemSelectionModal from '@/components/driveToStore/LineItemSelectionModal/LineItemSelectionModal';
import PoiSelectionModal from '@/components/driveToStore/PoiSelectionModal/PoiSelectionModal';
import { isSharedReportSession } from '@/helpers/shareReport/shareReport.helper';
import { useGlobalStore } from '@/store/global';
import { getSession } from '@/store/global/session/selectors';

import type { Poi } from '@/types/footfall';

import './brandStoreSummary.scss';

type Props = {
  pois: Poi[];
  entityCountries: string[];
  entityType: string;
  entityId: string;
  country?: string;
  lineItemName?: string;
  sharedReportFullAccess?: boolean;
  showUpliftOnlyTag?: boolean;
  userId: string;
};

type BrandGroup = {
  brand: string;
  count: number;
};

function groupByBrand(pois: Poi[]): BrandGroup[] {
  const map = new Map<string, number>();
  for (const poi of pois) {
    const brand = poi.brand ?? 'Unknown';
    map.set(brand, (map.get(brand) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([brand, count]) => ({ brand, count }))
    .sort((a, b) => b.count - a.count);
}

export default function BrandStoreSummary({
  pois,
  entityCountries,
  entityType,
  entityId,
  country,
  lineItemName,
  sharedReportFullAccess,
  showUpliftOnlyTag,
  userId
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false);
  const isShared = isSharedReportSession(useGlobalStore(getSession));
  const canOpenSingleReport = !isShared || sharedReportFullAccess;
  const { activeEntityIds } = useDriveToStoreNavigation();
  const poiSelectionButtonId = useId();
  const overflowTooltipId = useId();
  const upliftTagId = useId();
  const lineItemSelectionTagId = useId();
  const breakdownRef = useRef<HTMLDivElement>(null);
  const [visibleItemCount, setVisibleItemCount] = useState<number | null>(null);

  const totalCount = pois.length;
  const brandGroups = groupByBrand(pois);
  const totalLineItemsCount = entityId.split(',').filter(Boolean).length;
  const selectedEntitiesCount = activeEntityIds?.length || totalLineItemsCount;
  const isMultiLineItemMode = totalLineItemsCount > 1;

  // biome-ignore lint/correctness/useExhaustiveDependencies: brandGroups is a trigger — re-measure DOM items when brand list changes
  useEffect(() => {
    const calculateVisibleItems = () => {
      if (!breakdownRef.current) {
        return;
      }
      const containerWidth = breakdownRef.current.clientWidth;
      const items = breakdownRef.current.querySelectorAll('.brandStoreSummary__breakdownItem');
      if (items.length === 0) {
        return;
      }
      let accumulatedWidth = 0;
      let visibleCount = 0;
      items.forEach((item, index) => {
        const itemWidth = (item as HTMLElement).offsetWidth;
        const separatorWidth = index > 0 ? 24 : 0;
        const totalItemWidth = itemWidth + separatorWidth;
        if (accumulatedWidth + totalItemWidth <= containerWidth) {
          accumulatedWidth += totalItemWidth;
          visibleCount += 1;
        }
      });
      setVisibleItemCount(Math.max(1, visibleCount));
    };

    calculateVisibleItems();
  }, [brandGroups]);

  const getSeparartor = () => <span className="brandStoreSummary__breakdownSeparator">|</span>;

  const boldChunk = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

  const subtitleDescription = !isMultiLineItemMode
    ? formatMessage({
        defaultMessage: 'Analysis of all stores in Line Item',
        id: 'driveToStore.aggregatedReport.brandStoreSummary.description'
      })
    : selectedEntitiesCount < totalLineItemsCount
      ? formatMessage(
          {
            defaultMessage: 'Across <b>{selected}</b> of <b>{total}</b> line items',
            id: 'driveToStore.aggregatedReport.brandStoreSummary.lineItemsCountFiltered'
          },
          { b: boldChunk, selected: selectedEntitiesCount, total: totalLineItemsCount }
        )
      : formatMessage(
          {
            defaultMessage: 'Across <b>{count}</b> line items',
            id: 'driveToStore.aggregatedReport.brandStoreSummary.lineItemsCount'
          },
          { b: boldChunk, count: totalLineItemsCount }
        );

  const poiSelectionButton = (
    <ButtonUI
      className="brandStoreSummary__poiSelectionButton"
      icon="fa-duotone fa-solid fa-store"
      iconPosition="left"
      id={poiSelectionButtonId}
      onClick={() => setIsModalOpen(true)}
      shape="squareRounded"
      size="small"
      text={formatMessage({
        defaultMessage: 'POI selection',
        id: 'driveToStore.aggregatedReport.brandStoreSummary.poiSelection'
      })}
      type="primary"
    />
  );

  const breakdownContent = brandGroups.length > 0 && (
    <div className="brandStoreSummary__breakdown" ref={breakdownRef}>
      {brandGroups.slice(0, visibleItemCount ?? brandGroups.length).map(({ brand, count }, index) => (
        <span className="brandStoreSummary__breakdownItem" key={brand}>
          {index > 0 && getSeparartor()}
          <span className="brandStoreSummary__breakdownLabel">
            {count} {brand}
          </span>
        </span>
      ))}
      {visibleItemCount !== null && visibleItemCount < brandGroups.length && (
        <>
          {getSeparartor()}
          <TooltipTag
            className="brandStoreSummary__overflowTooltip"
            id={overflowTooltipId}
            status="information"
            tagConfig={{
              tagChildren: `+${brandGroups.length - visibleItemCount}`
            }}
            tooltipConfig={{
              tooltipEvent: 'mouseenter',
              tooltipPlace: 'bottom'
            }}
          >
            <div className="brandStoreSummary__overflowTooltipContent">
              {brandGroups.slice(visibleItemCount).map(({ brand, count }) => (
                <div className="brandStoreSummary__overflowItem" key={brand}>
                  <span>
                    {count} {brand}
                  </span>
                </div>
              ))}
            </div>
          </TooltipTag>
        </>
      )}
    </div>
  );

  return (
    <div className="brandStoreSummary">
      <div className="brandStoreSummary__content">
        <div className="brandStoreSummary__subtitle">
          <span className="brandStoreSummary__subtitleHeading">
            {formatMessage({
              defaultMessage: 'Aggregated Report',
              id: 'driveToStore.aggregatedReport.brandStoreSummary.heading'
            })}
          </span>
          <span className="brandStoreSummary__subtitleDescription">{subtitleDescription}</span>
          {!isShared && isMultiLineItemMode && (
            <ButtonUI
              icon="fa-solid fa-list-ul"
              iconPosition="left"
              id={lineItemSelectionTagId}
              onClick={() => setIsLineItemModalOpen(true)}
              size="ultraSmall"
              text={formatMessage(
                {
                  defaultMessage: 'Line Item selection',
                  id: 'driveToStore.aggregatedReport.brandStoreSummary.lineItemSelection'
                },
                { count: selectedEntitiesCount }
              )}
              type="secondary"
            />
          )}
        </div>
        <div className="brandStoreSummary__divider" />
        <div className="brandStoreSummary__metrics">
          <div className="brandStoreSummary__metricsMain">
            <span className="brandStoreSummary__totalCount">
              {formatMessage(
                {
                  defaultMessage: '{count} total POIs',
                  id: 'driveToStore.aggregatedReport.brandStoreSummary.totalPois'
                },
                { count: totalCount.toLocaleString('en-US') }
              )}
            </span>
            {showUpliftOnlyTag && (
              <Tag
                className="brandStoreSummary__upliftTag"
                icon={{
                  position: 'left',
                  webFont: 'fa-regular fa-circle-info'
                }}
                id={upliftTagId}
                status="information"
              >
                {formatMessage({
                  defaultMessage: 'Showing only POIs with uplift',
                  id: 'driveToStore.aggregatedReport.brandStoreSummary.showingOnlyUplift'
                })}
              </Tag>
            )}
          </div>
          {showUpliftOnlyTag && canOpenSingleReport ? (
            <div className="brandStoreSummary__metricsSecondRow">
              {breakdownContent}
              {poiSelectionButton}
            </div>
          ) : (
            breakdownContent
          )}
        </div>
        {!showUpliftOnlyTag && poiSelectionButton}
      </div>
      <PoiSelectionModal
        country={country}
        entityCountries={entityCountries}
        entityId={entityId}
        entityType={entityType}
        isOpen={isModalOpen}
        lineItemName={lineItemName}
        onClose={() => setIsModalOpen(false)}
        userId={userId}
      />
      <LineItemSelectionModal
        country={country}
        entityId={entityId}
        entityType={entityType}
        isOpen={isLineItemModalOpen}
        onClose={() => setIsLineItemModalOpen(false)}
        userId={userId}
      />
    </div>
  );
}
